/* ============================================================================
   SK CUSTOM CART DRAWER
   A from-scratch slide-out cart for Star Kagune. No app, no subscription.
   Talks directly to Shopify's Cart AJAX API and renders client-side.
   ----------------------------------------------------------------------------
   Modules:
     - Slide-out drawer (open/close, overlay, focus, esc)
     - Line items with qty steppers + remove
     - Tiered reward / free-shipping progress bar
     - Collection-matched upsell carousel (Shopify recommendations API)
     - Shipping Protection toggle (adds/removes a real variant)
     - Sticky checkout footer with live subtotal
   ========================================================================== */

(function () {
  "use strict";

  /* ---- Read merchant config injected by the section -------------------- */
  function readConfig() {
    const el = document.getElementById("sk-cart-config");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error("[SK Cart] bad config JSON", e);
      return {};
    }
  }

  /* ---- Anime cross-sell logic (ported from sk-anime-upsell.js) -------- */
  const CATEGORY_KEYWORDS = {
    "mini-figure": ["mini figure", "minifigure", "mini-figure", "custom figure", "giant figure"],
    led: ["led", "light box", "lightbox", "rgb"],
    rug: ["rug"],
    tapestry: ["tapestry", "tapestries", "woven tapestry"],
    keychain: ["keychain", "key chain", "key strap", "keystrap", "embroidered keychain"],
    poster: ["poster", "3d poster"],
    apparel: ["shirt", "hoodie", "jersey", "lever belt"],
  };
  const CROSS_SELL_TARGETS = {
    "mini-figure": ["tapestry", "led", "rug"],
    led: ["tapestry", "rug"],
    rug: ["tapestry", "led"],
    tapestry: ["led", "rug"],
    keychain: ["tapestry", "led", "rug"],
    poster: ["tapestry", "led", "rug"],
    apparel: ["tapestry", "led", "rug"],
    default: ["tapestry", "led", "rug"],
  };
  const COLLECTION_SUFFIX = { led: "-led-boxes", rug: "-rugs" };
  const CAT_LABELS = { led: "LED BOX", rug: "RUG", tapestry: "TAPESTRY", "mini-figure": "MINI FIGURE", keychain: "KEY STRAP", poster: "POSTER", apparel: "APPAREL" };
  const STATS_KEY = "sk_cart_stats_v1"; // browser-local cart metrics (like upsell-modal's sk_stats)
  // Fallback retail values (cents) for the gift value-stack, keyed by variant id.
  // Used when the theme-editor "value" setting isn't present (the live theme's
  // open customizer can strip newly-added settings on auto-save).
  const GIFT_VALUE_CENTS = {
    46737223090394: 2500, // Classified Minifigure
    47977154150618: 3900, // Mystery Framed 3D Art (rebuilt, untracked)
    47977154216154: 1000, // Grail Case (rebuilt, untracked)
    47977154183386: 6700, // Mystery RGB Lightbox (rebuilt, untracked)
  };

  function skSlugify(s) {
    return s.toLowerCase().replace(/['']/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }

  class SKCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.cfg = readConfig();
      this.cart = null;
      this.busy = false;
      this._jsonCache = {};
      this._upsellSig = "";
      this.activeTrigger = null;
    }

    connectedCallback() {
      this.overlay = this.querySelector(".sk-cart__overlay");
      this.panel = this.querySelector(".sk-cart__panel");
      this.body = this.querySelector(".sk-cart__body");

      // Close interactions
      this.overlay && this.overlay.addEventListener("click", () => this.close());
      this.addEventListener("click", (e) => {
        if (e.target.closest("[data-sk-close]")) this.close();
      });
      document.addEventListener("keyup", (e) => {
        if (e.key === "Escape" && this.classList.contains("is-open")) this.close();
      });

      // Delegated controls inside the drawer
      this.addEventListener("click", (e) => this.onClick(e));
      this.addEventListener("change", (e) => this.onChange(e));
      this.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && e.target.matches && e.target.matches(".sk-qty__input")) {
          e.preventDefault();
          e.target.blur(); // commit the typed quantity
        }
      });

      this.bindGlobalTriggers();

      // Self-healing scroll lock: if the page is ever locked while our cart is
      // NOT open (e.g. the hidden native drawer grabbed `overflow-hidden`, or a
      // stale lock from any race), strip it so the page can always scroll.
      const healLock = () => {
        if (this.classList.contains("is-open")) return;
        if (document.body.classList.contains("sk-cart-locked")) document.body.classList.remove("sk-cart-locked");
        if (document.body.classList.contains("overflow-hidden")) document.body.classList.remove("overflow-hidden");
        if (document.body.style.overflow === "hidden") document.body.style.overflow = "";
      };
      this._lockObserver = new MutationObserver(healLock);
      this._lockObserver.observe(document.body, { attributes: true, attributeFilter: ["class", "style"] });
      healLock();

      // Prime cart state (no open)
      this.refresh({ open: false });

      // Public API + cross-script events.
      // These only SYNC the cart (open:false) — they must never force it open,
      // or the theme firing cart:ajax-update after an add would re-open the
      // drawer the user just closed (and re-lock scrolling). Auto-open on add is
      // handled solely by the network hook. Use window.SKCart.open() to force open.
      window.SKCart = this;
      document.addEventListener("sk-cart:refresh", () => this.refresh({ open: false }));
      document.addEventListener("cart:ajax-update", () => this.refresh({ open: false }));

      // Local stats viewer: sk_cart_stats() in console, or ?sk_cart_stats=1 in URL.
      window.sk_cart_stats = () => this.showStatsViewer();
      if (/[?&]sk_cart_stats=1/.test(location.search)) setTimeout(() => this.showStatsViewer(), 500);
    }

    /* ---- Trigger wiring: open on cart click, intercept add-to-cart ----- */
    bindGlobalTriggers() {
      // Open the drawer from any cart toggle (capture so we beat theme JS)
      document.addEventListener(
        "click",
        (e) => {
          const trigger = e.target.closest(
            '#cart-icon-bubble, a[href="/cart"], a[href^="/cart?"], [data-sk-cart-open]'
          );
          if (trigger) {
            e.preventDefault();
            e.stopPropagation();
            this.open(trigger);
          }
        },
        true
      );

      // NOTE: We deliberately do NOT intercept add-to-cart form submits.
      // That lets the pop-up upsell modal (upsell-modal.js) run first.
      // The cart opens automatically AFTER any add completes, via the
      // network hook below — so the flow is: ATC -> upsell modal -> cart.

      this.installCartHooks();
    }

    /* ---- Catch-all: detect cart mutations from ANY source -------------- */
    installCartHooks() {
      if (window.__skCartHooked) return;
      window.__skCartHooked = true;
      const self = this;
      const MUT = /\/cart\/(add|change|update|clear)(\.js|\b)/;
      const ADD = /\/cart\/add/;

      const onMutation = (url) => {
        if (self._selfOp) return; // ignore our own calls (handled directly)
        if (ADD.test(url)) {
          // An add opens the cart (own timer, so /update calls can't reschedule it late)
          clearTimeout(self._extOpenTimer);
          self._extOpenTimer = setTimeout(() => self.refresh({ open: true }), 200);
        } else {
          // update / change / clear: refresh only, never auto-open
          clearTimeout(self._extRefreshTimer);
          self._extRefreshTimer = setTimeout(() => self.refresh({ open: false }), 300);
        }
      };

      const origFetch = window.fetch;
      window.fetch = function (input, init) {
        const url = typeof input === "string" ? input : (input && input.url) || "";
        const p = origFetch.apply(this, arguments);
        if (MUT.test(url)) p.then((res) => { if (res && res.ok) onMutation(url); }).catch(() => {});
        return p;
      };

      const origOpen = XMLHttpRequest.prototype.open;
      const origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__skUrl = url || "";
        return origOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        const xhr = this;
        if (MUT.test(xhr.__skUrl || "")) {
          xhr.addEventListener("load", () => { if (xhr.status >= 200 && xhr.status < 300) onMutation(xhr.__skUrl); });
        }
        return origSend.apply(this, arguments);
      };
    }

    /* ---- Cart AJAX helpers --------------------------------------------- */
    async getCart() {
      const res = await fetch(`${this.routes().cart}.js`, { headers: { Accept: "application/json" } });
      return res.json();
    }

    async addItems(items) {
      this._selfOp = true;
      try {
        const res = await fetch(`${this.routes().add}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items }),
        });
        if (res.status === 422) {
          const err = await res.json();
          this.flash(err.description || "Could not add item.");
          throw new Error(err.description);
        }
        return res.json();
      } finally {
        this._selfOp = false;
      }
    }

    async changeLine(line, quantity) {
      this._selfOp = true;
      try {
        const res = await fetch(`${this.routes().change}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ line, quantity }),
        });
        return res.json();
      } finally {
        this._selfOp = false;
      }
    }

    routes() {
      const r = (window.routes && window.routes) || {};
      return {
        cart: r.cart_url || "/cart",
        add: r.cart_add_url || "/cart/add",
        change: r.cart_change_url || "/cart/change",
      };
    }

    /* ---- High-level actions -------------------------------------------- */
    async addFromForm(form) {
      const fd = new FormData(form);
      const id = fd.get("id");
      if (!id) return;
      const qty = parseInt(fd.get("quantity") || "1", 10);
      const props = {};
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m) props[m[1]] = v;
      }
      const item = { id: Number(id), quantity: qty };
      if (Object.keys(props).length) item.properties = props;
      this.open(); // open instantly with current content; content updates after add
      try {
        await this.addItems([item]);
        await this.refresh({ open: true });
      } catch (e) {
        /* flashed already */
      }
    }

    async addVariant(variantId, quantity = 1) {
      try {
        await this.addItems([{ id: Number(variantId), quantity }]);
        await this.refresh({ open: true });
      } catch (e) {
        /* flashed already */
      }
    }

    async setLineQty(line, quantity) {
      await this.changeLine(line, quantity);
      await this.refresh({ open: false });
    }

    // Number of distinct "real" product lines (excludes protection + free gifts).
    realItemCount() {
      const protId = Number(this.cfg.protection && this.cfg.protection.variantId);
      return this.cart.items.filter(
        (i) => i.variant_id !== protId && !this.isFreeReward(i)
      ).length;
    }

    // Badge count = total UNITS of real products (excludes free gift + protection),
    // so an auto-added Mystery Minifigure / Collector Case never inflates the count.
    displayCount() {
      const protId = Number(this.cfg.protection && this.cfg.protection.variantId);
      return this.cart.items
        .filter((i) => i.variant_id !== protId && !this.isFreeReward(i))
        .reduce((s, i) => s + i.quantity, 0);
    }

    // If the only things left are protection/gifts (no real products), remove
    // them so the cart truly empties and shows the empty state — never a lonely
    // $5 protection with no products.
    async cleanupOrphans() {
      if (this._cleaning) return false;
      if (this.cart.item_count === 0 || this.realItemCount() > 0) return false;
      this._cleaning = true;
      try {
        for (let idx = this.cart.items.length; idx >= 1; idx--) {
          await this.changeLine(idx, 0);
        }
        this.cart = await this.getCart();
        return true;
      } finally {
        this._cleaning = false;
      }
    }

    async refresh({ open = false } = {}) {
      this.cart = await this.getCart();
      // Render + open immediately with fresh cart (empty state shows as soon as
      // there are no real products, even before orphan cleanup finishes).
      this.render();
      this.syncBubble();
      // Suppress AUTO-open briefly after the user closes, so the post-add event/
      // fetch cascade can't re-open the drawer they just dismissed.
      if (open && Date.now() - (this._closedAt || 0) > 1200) this.open();
      // Reconcile the $0 gift, then strip any orphaned protection/gift (runs
      // AFTER reconcile with its own flag, so a reconcile can't block cleanup).
      if (!this._reconciling) {
        const changed = await this.reconcileGifts();
        if (changed) this.cart = await this.getCart();
      }
      const cleaned = await this.cleanupOrphans();
      if (cleaned) this.cart = await this.getCart();
      this.render();
      this.syncBubble();
    }

    /* ---- Shipping protection ------------------------------------------- */
    protectionLine() {
      if (!this.cart) return null;
      const vid = Number(this.cfg.protection && this.cfg.protection.variantId);
      if (!vid) return null;
      const idx = this.cart.items.findIndex((i) => i.variant_id === vid);
      return idx === -1 ? null : { item: this.cart.items[idx], line: idx + 1 };
    }

    async toggleProtection(on) {
      const vid = Number(this.cfg.protection && this.cfg.protection.variantId);
      if (!vid) return;
      const existing = this.protectionLine();
      this.setLoading(true);
      if (on && !existing) {
        await this.addItems([{ id: vid, quantity: 1 }]);
        this.statBump("protectionAdds");
      } else if (!on && existing) {
        await this.changeLine(existing.line, 0);
      }
      await this.refresh({ open: true });
      this.setLoading(false);
    }

    /* ---- Reward math (excludes protection + free gifts) ---------------- */
    giftVariantIds() {
      return (this.cfg.tiers || [])
        .map((t) => Number(t.freeVariantId))
        .filter(Boolean);
    }

    // True ONLY for a free-gift line WE auto-added (flagged with the _sk_gift
    // line property). A customer who buys the same product (e.g. the Collector
    // Display Case) themselves has NO flag, so it's treated as a normal paid
    // item and never auto-removed / hidden / zeroed.
    isAutoGift(item) {
      if (!item || !item.properties) return false;
      const f = item.properties._sk_gift;
      return f === "1" || f === 1 || f === true;
    }

    // True when a line should DISPLAY/COUNT as a free reward: either we
    // auto-added it (flagged), OR it's a reward-tier variant the customer has
    // that a reward discount has zeroed to $0 (e.g. the Collector Case added
    // via the on-page add-on toggle, then made free at $100). Restricting the
    // "$0" rule to reward variants means a regular product that's free via the
    // "Buy 3 Get 2" promo is NOT mistaken for a gift (keeps its qty stepper).
    isFreeReward(item) {
      if (this.isAutoGift(item)) return true;
      if (!item) return false;
      return this.giftVariantIds().includes(item.variant_id) && item.final_line_price === 0;
    }

    // Configured retail value (in cents) of a gift, for the value-stack display.
    giftValueCents(item) {
      if (!item) return 0;
      const tier = (this.cfg.tiers || []).find((t) => Number(t.freeVariantId) === item.variant_id);
      const fromCfg = tier ? Number(tier.value) || 0 : 0;
      return fromCfg || GIFT_VALUE_CENTS[item.variant_id] || 0;
    }
    // Sum of retail values of every free reward currently in the cart (the "stack").
    unlockedGiftValueCents() {
      if (!this.cart) return 0;
      return this.cart.items.reduce((s, i) => s + (this.isFreeReward(i) ? this.giftValueCents(i) : 0), 0);
    }

    // "You've unlocked $X in free gifts" — shown at the top under the progress bar.
    tplGiftStack() {
      const v = this.unlockedGiftValueCents();
      return v > 0
        ? `<div class="sk-foot__giftstack sk-foot__giftstack--top">🎁 <strong>+ ${this.money(v)}</strong> in free gifts unlocked</div>`
        : "";
    }

    qualifyingTotal() {
      if (!this.cart) return 0;
      const vid = Number(this.cfg.protection && this.cfg.protection.variantId);
      return this.cart.items.reduce((sum, i) => {
        if (i.variant_id === vid) return sum;
        if (this.isFreeReward(i)) return sum; // free rewards don't count toward tiers
        return sum + i.final_line_price;
      }, 0);
    }

    /* ---- Free-gift auto-add / remove on threshold ---------------------- */
    async reconcileGifts() {
      const tiers = (this.cfg.tiers || []).filter((t) => Number(t.freeVariantId));
      if (!tiers.length) return false;
      const total = this.qualifyingTotal();
      let changed = false;
      this._reconciling = true;
      try {
        // 1) REMOVALS first — strip our own flagged gifts no longer earned.
        //    Never touches a paid copy (isAutoGift only). Loop re-finds the
        //    index each time so shifting line numbers can't skip one.
        for (const t of tiers) {
          const vid = Number(t.freeVariantId);
          if (total >= Number(t.threshold)) continue;
          let idx;
          while ((idx = this.cart.items.findIndex((i) => i.variant_id === vid && this.isAutoGift(i))) !== -1) {
            await this.changeLine(idx + 1, 0);
            this.cart = await this.getCart();
            changed = true;
          }
        }
        // 2) ADDITIONS — earned gifts the customer doesn't already have, added
        //    in ONE atomic call so two sequential adds can't race Shopify's
        //    discount engine (which occasionally dropped the 2nd add).
        const toAdd = tiers
          .filter((t) => {
            const vid = Number(t.freeVariantId);
            const earned = total >= Number(t.threshold);
            const present = this.cart.items.some((i) => i.variant_id === vid);
            return earned && !present;
          })
          .map((t) => ({ id: Number(t.freeVariantId), quantity: 1, properties: { _sk_gift: "1" } }));
        if (toAdd.length) {
          await this.addItems(toAdd);
          this.cart = await this.getCart();
          // Verify each landed; retry once for any Shopify silently dropped.
          const missing = toAdd.filter((a) => !this.cart.items.some((i) => i.variant_id === a.id));
          if (missing.length) {
            await this.addItems(missing);
            this.cart = await this.getCart();
          }
          toAdd.forEach(() => this.statBump("giftUnlocks"));
          changed = true;
        }
      } finally {
        this._reconciling = false;
      }
      return changed;
    }

    /* ===================================================================
       RENDER
       =================================================================== */
    render() {
      if (!this.cart) return;
      const empty = this.realItemCount() === 0;
      this.classList.toggle("is-empty", empty);

      this.body.innerHTML = empty
        ? [this.tplProgress(), this.tplEmpty()].join("")
        : [
            this.tplProgress(),
            this.tplItems(),
            this.tplUpsells(),
          ].join("");

      const footer = this.querySelector(".sk-cart__footer");
      if (footer) footer.innerHTML = empty ? "" : this.tplFooter();

      // Title count — products the customer is buying (excludes free gift + protection)
      const count = this.querySelector("[data-sk-count]");
      if (count) count.textContent = this.displayCount();

      if (empty) this.loadEmptyUpsells();
      else this.syncUpsells();
    }

    // Signature of the "real" items (drives upsell anime context)
    upsellSignature() {
      const protId = Number(this.cfg.protection && this.cfg.protection.variantId);
      return this.cart.items
        .filter((i) => i.variant_id !== protId && !this.isFreeReward(i))
        .map((i) => i.handle)
        .sort()
        .join("|");
    }

    // Reuse cached upsell DOM when the item context is unchanged (no flash on
    // protection toggle / qty change / gift add); only re-fetch when it changes.
    syncUpsells() {
      const wrap = this.querySelector("[data-sk-upsells]");
      const list = this.querySelector("[data-sk-upsell-list]");
      const headEl = this.querySelector("[data-sk-upsell-heading]");
      if (!wrap || !list) return;
      const sig = this.upsellSignature();
      if (sig === this._upsellSig && this._upsellHTML != null) {
        if (this._upsellHTML === "") { wrap.hidden = true; return; }
        headEl.textContent = this._upsellHeading || "";
        list.innerHTML = this._upsellHTML;
        wrap.hidden = false;
        return;
      }
      this.loadUpsells();
    }

    tplEmpty() {
      const e = this.cfg.empty || {};
      const cta =
        e.ctaLabel && e.ctaLink
          ? `<a class="sk-empty__cta" href="${e.ctaLink}">${this.escape(e.ctaLabel)}</a>`
          : "";
      return `
        <div class="sk-cart__empty">
          <p class="sk-empty__headline">${this.escape(e.headline || "You came here for a reason.")}</p>
          ${cta}
        </div>
        <div class="sk-cart__upsells sk-cart__upsells--empty" data-sk-empty-upsells hidden>
          <p class="sk-upsell__heading">${this.escape(e.upsellHeading || "Collectors also added")}</p>
          <div class="sk-upsell__list" data-sk-empty-list></div>
        </div>`;
    }

    async loadEmptyUpsells() {
      const e = this.cfg.empty || {};
      const limit = e.limit || 6;
      const handles = (e.collections || e.collection || "leds,rugs,woven-tapestries")
        .split(",").map((s) => s.trim()).filter(Boolean);
      const wrap = this.querySelector("[data-sk-empty-upsells]");
      const list = this.querySelector("[data-sk-empty-list]");
      if (!wrap || !list) return;

      const shuffle = (a) => {
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      };
      const perCat = Math.max(1, Math.round(limit / handles.length));
      const seen = new Set();
      const buckets = [];
      for (const h of handles) {
        const pool = (await this.getCollectionProducts(h, 16)).filter(
          (p) => p.variants && p.variants.some((v) => v.available)
        );
        // Rotate: randomly pick `perCat` from each category's TOP sellers (top ~12)
        const pick = shuffle(pool.slice(0, 12))
          .filter((p) => (seen.has(p.id) ? false : (seen.add(p.id), true)))
          .slice(0, perCat);
        pick.forEach((p) => (p._upsellCat = this.skDetectCategory(p)));
        buckets.push(pick);
      }
      // Interleave the categories: LED, rug, tapestry, LED, rug, tapestry…
      const chosen = [];
      let round = 0;
      while (chosen.length < limit) {
        let added = false;
        for (const b of buckets) {
          if (chosen.length >= limit) break;
          if (round < b.length) { chosen.push(b[round]); added = true; }
        }
        if (!added) break;
        round++;
      }
      if (!chosen.length) { wrap.hidden = true; return; }
      list.innerHTML = chosen.map((p) => this.tplUpsellCard(p)).join("");
      wrap.hidden = false;
    }

    /* ---- Tiered progress bar (minimal) --------------------------------- */
    tplProgress() {
      const tiers = (this.cfg.tiers || []).filter((t) => t.enabled !== false && t.threshold != null);
      if (!tiers.length) return "";
      tiers.sort((a, b) => a.threshold - b.threshold);
      const total = this.qualifyingTotal();
      const goal = tiers[tiers.length - 1].threshold;
      const pct = Math.min(100, (total / goal) * 100);

      // Headline: name the next unmet reward, or the unlocked state
      const next = tiers.find((t) => total < t.threshold);
      let headline;
      if (next) {
        const missing = this.money(next.threshold - total);
        headline = (next.awayText || "[amount] away from [reward]")
          .replace("[amount]", `<strong>${missing}</strong>`)
          .replace("[reward]", `<span class="sk-prog__reward">${this.escape(next.label)}</span>`);
      } else {
        headline = this.cfg.allUnlockedText || "All rewards unlocked";
      }

      // Milestone ICONS (gift / case / truck) that fill as you pass them
      const ticks = tiers
        .map((t) => {
          const reached = total >= t.threshold;
          const leftPct = Math.min(100, (t.threshold / goal) * 100);
          return `<span class="sk-prog__node ${reached ? "is-reached" : ""}" style="left:${leftPct}%"><span class="sk-prog__icon">${this.tierIcon(t)}</span></span>`;
        })
        .join("");

      // Pin the final reward at the finish line (only while not all unlocked)
      const last = tiers[tiers.length - 1];
      const endcap = next
        ? `<div class="sk-prog__endcap">Final prize: <b>${this.escape(last.label)} · ${this.money(last.threshold)}</b></div>`
        : "";

      return `
        <div class="sk-cart__progress">
          <p class="sk-prog__headline">${headline}</p>
          <div class="sk-prog__track sk-prog__track--icons">
            <div class="sk-prog__fill" style="width:${pct}%"></div>
            ${ticks}
          </div>
          ${endcap}
        </div>`;
    }

    tierIcon(t) {
      const which = t.icon || (t.type === "free_shipping" ? "truck" : "gift");
      const icons = {
        truck: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 3h13v11H1zM14 7h4l3 3v4h-7"/><circle cx="6" cy="18" r="1.6"/><circle cx="18" cy="18" r="1.6"/></svg>',
        gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7S8 7 8 4.5 12 4 12 7zM12 7s4 0 4-2.5S12 4 12 7z"/></svg>',
        box: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 8l9-5 9 5-9 5-9-5zM3 8v8l9 5 9-5V8M12 13v8"/></svg>',
        star: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l3 6.5 7 .9-5 4.8 1.3 7-6.3-3.4L5.7 21l1.3-7-5-4.8 7-.9z"/></svg>',
        led: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2.3h6c0-1.1.4-1.8 1-2.3A7 7 0 0 0 12 2z"/></svg>',
        poster: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="3" width="16" height="18" rx="1"/><circle cx="9" cy="8" r="1.3"/><path d="M5 16l4-4 3 3 3-4 4 5"/></svg>',
      };
      return icons[which] || icons.gift;
    }

    /* ---- Line items ----------------------------------------------------- */
    tplItems() {
      const rows = this.cart.items
        .map((item, i) => {
          const line = i + 1;
          const isProtection =
            item.variant_id === Number(this.cfg.protection && this.cfg.protection.variantId);
          if (isProtection) return ""; // protection shown in its own module
          // Free gift line — name / category / FREE GIFT tag, "Free" on the right.
          // Any reward that's currently free (flagged gift, or a reward variant
          // a discount zeroed). A reward variant the customer is PAYING for
          // (e.g. case under $100) falls through to the normal item row.
          if (this.isFreeReward(item)) {
            const gv = item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : (item.product_type || "Reward");
            const val = this.giftValueCents(item);
            const freeHtml = val > 0
              ? `<span class="sk-gift-was">${this.money(val)}</span>FREE`
              : "Free";
            return `
            <div class="sk-item sk-item--gift">
              <a class="sk-item__img"><img src="${this.imgUrl(item.image, 160)}" alt=""></a>
              <div class="sk-item__info">
                <p class="sk-item__title">${this.escape(item.product_title)}</p>
                <p class="sk-item__variant">${this.escape(gv)}</p>
                <span class="sk-gift-tag">Free Gift</span>
              </div>
              <div class="sk-item__free">${freeHtml}</div>
            </div>`;
          }
          // RIDGE-style: name → category → price stacked; qty stepper on the right; no trash.
          const cat = item.options_with_values && item.options_with_values.length && item.product_has_only_default_variant === false
            ? item.options_with_values.map((o) => this.escape(o.value)).join(" / ")
            : this.escape(item.product_type || "");
          const compare =
            item.original_line_price > item.final_line_price
              ? `<span class="sk-item__was">${this.money(item.original_line_price)}</span>`
              : "";
          return `
          <div class="sk-item" data-line="${line}">
            <a href="${item.url}" class="sk-item__img">
              <img src="${this.imgUrl(item.image, 200)}" alt="${this.escape(item.product_title)}" decoding="async">
            </a>
            <div class="sk-item__info">
              <p class="sk-item__title">${this.escape(item.product_title)}</p>
              ${cat ? `<p class="sk-item__variant">${cat}</p>` : ""}
              <p class="sk-item__price">${compare}<span class="sk-item__now">${this.money(item.final_line_price)}</span></p>
            </div>
            <div class="sk-qty">
              <button type="button" data-sk-dec aria-label="Decrease">−</button>
              <input class="sk-qty__input" type="text" inputmode="numeric" value="${item.quantity}" aria-label="Quantity">
              <button type="button" data-sk-inc aria-label="Increase">+</button>
            </div>
          </div>`;
        })
        .join("");
      return `<div class="sk-cart__items">${rows}</div>`;
    }

    /* ---- Upsells: anime cross-sell (ported from sk-anime-upsell) ------- */
    tplUpsells() {
      if (this.cfg.upsell && this.cfg.upsell.enabled === false) return "";
      return `
        <div class="sk-cart__upsells" data-sk-upsells hidden>
          <p class="sk-upsell__heading" data-sk-upsell-heading></p>
          <div class="sk-upsell__list" data-sk-upsell-list></div>
        </div>`;
    }

    async skFetchJSON(url) {
      if (this._jsonCache[url]) return this._jsonCache[url];
      try {
        const r = await fetch(url);
        if (!r.ok) return null;
        const data = await r.json();
        if (data) this._jsonCache[url] = data;
        return data;
      } catch (e) {
        return null;
      }
    }
    async getCollectionProducts(handle, limit) {
      const d = await this.skFetchJSON(`/collections/${handle}/products.json?limit=${limit || 8}`);
      return d && d.products ? d.products : [];
    }
    skDetectCategory(product) {
      const combined = `${product.title || ""} ${product.product_type || ""}`.toLowerCase();
      for (const cat in CATEGORY_KEYWORDS) {
        for (const kw of CATEGORY_KEYWORDS[cat]) if (combined.indexOf(kw) !== -1) return cat;
      }
      return "default";
    }
    // The anime tag is the one whose `{slug}-led-boxes` collection exists.
    async findAnimeSlug(product) {
      let tags = product.tags || [];
      if (typeof tags === "string") tags = tags.split(",").map((t) => t.trim());
      const slugs = tags.map((t) => ({ tag: t, slug: skSlugify(t) })).filter((s) => s.slug.length >= 2);
      for (const s of slugs) {
        const prods = await this.getCollectionProducts(`${s.slug}-led-boxes`, 1);
        if (prods.length > 0) return s;
      }
      return null;
    }
    async getTapestryProducts(animeTag) {
      const prods = await this.getCollectionProducts("woven-tapestries", 50);
      const lowerTag = animeTag.toLowerCase();
      return prods.filter((p) => {
        let tags = p.tags || [];
        if (typeof tags === "string") tags = tags.split(",").map((t) => t.trim());
        return tags.some((t) => t.toLowerCase() === lowerTag);
      });
    }
    async getCrossUpsells(animeSlug, animeTag, cartCategory, cartHandles, maxTotal) {
      maxTotal = maxTotal || 3;
      const targets = CROSS_SELL_TARGETS[cartCategory] || CROSS_SELL_TARGETS.default;
      const seen = {};
      const perCat = {};
      for (const target of targets) {
        let prods;
        if (target === "tapestry") prods = await this.getTapestryProducts(animeTag);
        else if (COLLECTION_SUFFIX[target]) prods = await this.getCollectionProducts(animeSlug + COLLECTION_SUFFIX[target], 8);
        else prods = [];
        const filtered = [];
        for (const p of prods) {
          if (cartHandles[p.handle] || seen[p.handle]) continue;
          if (!(p.variants && p.variants.some((v) => v.available))) continue;
          p._upsellCat = target;
          seen[p.handle] = true;
          filtered.push(p);
        }
        perCat[target] = filtered;
      }
      const results = [];
      for (const t of targets) if (results.length < maxTotal && perCat[t] && perCat[t].length) results.push(perCat[t].shift());
      for (const t of targets) while (results.length < maxTotal && perCat[t] && perCat[t].length) results.push(perCat[t].shift());

      // Fallback: if the cross-sell categories don't fill the row (e.g. an anime
      // that only has LED boxes, like Avatar), backfill with OTHER products from
      // the same anime's main collection so we never show an empty upsell row.
      if (results.length < maxTotal) {
        const main = await this.getCollectionProducts(animeSlug, 24);
        for (const p of main) {
          if (results.length >= maxTotal) break;
          if (cartHandles[p.handle] || seen[p.handle]) continue;
          if (!(p.variants && p.variants.some((v) => v.available))) continue;
          p._upsellCat = this.skDetectCategory(p);
          seen[p.handle] = true;
          results.push(p);
        }
      }
      return results;
    }

    async loadUpsells() {
      if (this.cfg.upsell && this.cfg.upsell.enabled === false) return;
      const wrap = this.querySelector("[data-sk-upsells]");
      const list = this.querySelector("[data-sk-upsell-list]");
      const headEl = this.querySelector("[data-sk-upsell-heading]");
      if (!wrap || !list) return;

      // Cache the context signature
      const sig = this.upsellSignature();
      this._upsellSig = sig;
      const hide = () => { this._upsellHTML = ""; this._upsellHeading = ""; wrap.hidden = true; };
      const paint = (heading, html) => {
        this._upsellHeading = heading; this._upsellHTML = html;
        if (this.upsellSignature() === sig) { headEl.textContent = heading; list.innerHTML = html; wrap.hidden = false; }
      };

      const protId = Number(this.cfg.protection && this.cfg.protection.variantId);
      const realItems = this.cart.items.filter((i) => i.variant_id !== protId && !this.isFreeReward(i));
      if (!realItems.length) return hide();

      const cartHandles = {};
      this.cart.items.forEach((i) => (cartHandles[i.handle] = true));
      const maxTotal = (this.cfg.upsell && this.cfg.upsell.limit) || 3;

      // Last-resort fallback: Shopify "related products" for the priciest item.
      // Guarantees an anime with no related catalog still shows *something*.
      const anchor = realItems.slice().sort((a, b) => b.final_line_price - a.final_line_price)[0];
      const fallback = async () => {
        if (!anchor) return hide();
        const data = await this.skFetchJSON(`/recommendations/products.json?product_id=${anchor.product_id}&limit=${maxTotal + 4}&intent=related`);
        const inCart = new Set(this.cart.items.map((i) => i.product_id));
        const recs = ((data && data.products) || [])
          .filter((p) => !inCart.has(p.id) && p.variants && p.variants.some((v) => v.available))
          .slice(0, maxTotal);
        if (!recs.length) return hide();
        recs.forEach((p) => (p._upsellCat = this.skDetectCategory(p)));
        paint("You may also like", recs.map((p) => this.tplUpsellCard(p)).join(""));
      };

      // Find all distinct anime present in the cart
      const animeList = [];
      const seenSlugs = {};
      for (const item of realItems) {
        const product = await this.skFetchJSON(`/products/${item.handle}.js`);
        if (!product) continue;
        const anime = await this.findAnimeSlug(product);
        if (anime && !seenSlugs[anime.slug]) {
          seenSlugs[anime.slug] = true;
          animeList.push({ anime, product });
        }
      }
      if (!animeList.length) return fallback();

      // Cross-sell per anime
      const perAnime = [];
      for (const entry of animeList) {
        const cartCat = this.skDetectCategory(entry.product);
        const ups = await this.getCrossUpsells(entry.anime.slug, entry.anime.tag, cartCat, cartHandles, maxTotal);
        if (ups.length) perAnime.push({ anime: entry.anime, upsells: ups });
      }
      if (!perAnime.length) return fallback();

      // Interleave round-robin across animes
      const finalProducts = [];
      let round = 0;
      while (finalProducts.length < maxTotal) {
        let added = false;
        for (const r of perAnime) {
          if (finalProducts.length >= maxTotal) break;
          if (round < r.upsells.length) { finalProducts.push(r.upsells[round]); added = true; }
        }
        if (!added) break;
        round++;
      }
      if (!finalProducts.length) return fallback();

      const titleTag = perAnime.length === 1 ? perAnime[0].anime.tag : null;
      const displayTag = titleTag ? titleTag.replace(/\b\w/g, (c) => c.toUpperCase()) : null;
      const heading = displayTag ? `Complete your ${displayTag} collection` : "Complete your collection";
      paint(heading, finalProducts.map((p) => this.tplUpsellCard(p)).join(""));
    }

    tplUpsellCard(p) {
      const avail = (p.variants || []).filter((x) => x.available);
      const v = avail[0] || (p.variants || [])[0];
      if (!v) return "";
      const rawImg = (p.images && p.images[0]) || p.featured_image || "";
      const img = typeof rawImg === "object" && rawImg ? rawImg.src || "" : rawImg;
      // Price unit differs by source: collection JSON = dollars string ("67.00"),
      // recommendations JSON = cents number (2500). Normalise both to cents.
      const toCents = (val) => (val == null || val === "" ? 0 : typeof val === "number" ? Math.round(val) : Math.round(parseFloat(val) * 100));
      const priceC = toCents(v.price);
      const compareC = toCents(v.compare_at_price);
      const hasDiscount = compareC > priceC;
      // Multi-variant products (e.g. key straps with character options) get an
      // inline picker so the customer can choose which variant to add.
      const multi = avail.length > 1;
      const selector = multi
        ? `<select class="sk-up-row__variant" aria-label="Choose option">
             ${avail.map((x) => `<option value="${x.id}">${this.escape(x.title)}</option>`).join("")}
           </select>`
        : "";
      return `
        <div class="sk-up-row">
          <a href="${p.url}" class="sk-up-row__img"><img src="${this.imgUrl(img, 160)}" alt="" decoding="async"></a>
          <div class="sk-up-row__info">
            <p class="sk-up-row__title">${this.escape(p.title)}</p>
            <p class="sk-up-row__type">${this.escape(CAT_LABELS[p._upsellCat] || (p._upsellCat || "").toUpperCase())}</p>
            <p class="sk-up-row__price">
              <span>${this.money(priceC)}</span>
              ${hasDiscount ? `<span class="sk-up-row__was">${this.money(compareC)}</span>` : ""}
            </p>
            ${selector}
          </div>
          <button type="button" class="sk-up-row__add" data-sk-add-variant="${v.id}">Add</button>
        </div>`;
    }

    /* ---- Shipping protection toggle ------------------------------------ */
    tplProtection() {
      const p = this.cfg.protection;
      if (!p || p.enabled === false || !p.variantId) return "";
      const on = !!this.protectionLine();
      return `
        <div class="sk-cart__protection">
          <div class="sk-prot__icon">${
            p.icon
              ? `<img src="${p.icon}" alt="">`
              : `<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 2l8 3v6c0 5-3.4 8.7-8 11-4.6-2.3-8-6-8-11V5z"/><path d="M9 12l2 2 4-4"/></svg>`
          }</div>
          <div class="sk-prot__text">
            <p class="sk-prot__title">${this.escape(p.heading || "Shipping Protection")}</p>
            <p class="sk-prot__desc">${this.escape(p.text || "")}</p>
          </div>
          <div class="sk-prot__right">
            <span class="sk-prot__price">${this.money(p.price || 0)}</span>
            <label class="sk-switch">
              <input type="checkbox" data-sk-protection ${on ? "checked" : ""}>
              <span class="sk-switch__slider"></span>
            </label>
          </div>
        </div>`;
    }

    /* ---- Footer --------------------------------------------------------- */
    // Amount actually charged: cart total minus free-gift lines whose price a
    // checkout discount will zero (e.g. the BXGY "FREE CASE"). The Mystery
    // Minifigure is already $0 so it subtracts nothing.
    displayTotal() {
      const giftSum = this.cart.items
        .filter((i) => this.isFreeReward(i))
        .reduce((s, i) => s + i.final_line_price, 0);
      return Math.max(0, this.cart.total_price - giftSum);
    }

    // Full retail before any discounts/promos/free gifts — so the strikethrough
    // "was" total includes the value of free rewards (shows the saving).
    originalTotal() {
      return this.cart.items.reduce((s, i) => s + i.original_line_price, 0);
    }

    tplFooter() {
      const label = (this.cfg.checkout && this.cfg.checkout.label) || "Protected Checkout";
      const total = this.displayTotal();
      const original = this.originalTotal();
      const saved = original - total;
      const savedPct = original > 0 && saved > 0 ? Math.round((saved / original) * 100) : 0;
      const savings = saved > 0
        ? `<span class="sk-foot__was">${this.money(original)}</span><span class="sk-foot__saved">${savedPct}% Saved</span>`
        : "";
      return `
        ${this.tplProtection()}
        <div class="sk-foot__row">
          <span class="sk-foot__label">Total <em>(Excluding Sales Tax)</em></span>
          <span class="sk-foot__totals"><span class="sk-foot__total">${this.money(total)}</span>${savings}</span>
        </div>
        <button type="button" class="sk-foot__checkout" data-sk-checkout>${this.escape(label)}</button>
        ${this.cfg.checkout && this.cfg.checkout.subtext ? `<p class="sk-foot__note">${this.escape(this.cfg.checkout.subtext)}</p>` : ""}
        ${this.tplTrustBar()}`;
    }

    tplTrustBar() {
      const raw = this.cfg.trustBar || "";
      const items = raw.split(",").map((s) => s.trim()).filter(Boolean);
      if (!items.length) return "";
      const cells = items.map((it) => {
        const parts = it.split(/\s+/);
        const first = parts.shift();
        const rest = parts.join(" ");
        return `<span class="sk-foot__trust-item"><strong>${this.escape(first)}</strong>${rest ? " " + this.escape(rest) : ""}</span>`;
      });
      return `<div class="sk-foot__trust">${cells.join('<span class="sk-foot__trust-sep">✦</span>')}</div>`;
    }

    /* ===================================================================
       EVENTS
       =================================================================== */
    onClick(e) {
      const itemEl = e.target.closest(".sk-item");
      if (itemEl) {
        const line = Number(itemEl.dataset.line);
        const item = this.cart.items[line - 1];
        if (e.target.closest("[data-sk-inc]")) return this.setLineQty(line, item.quantity + 1);
        if (e.target.closest("[data-sk-dec]")) return this.setLineQty(line, item.quantity - 1);
        if (e.target.closest("[data-sk-remove]")) return this.setLineQty(line, 0);
      }
      const addBtn = e.target.closest("[data-sk-add-variant]");
      if (addBtn) {
        // Use the chosen variant if the row has an inline picker
        const sel = addBtn.closest(".sk-up-row")?.querySelector(".sk-up-row__variant");
        const variantId = sel ? sel.value : addBtn.dataset.skAddVariant;
        addBtn.classList.add("sk-busy");
        this.statBump("upsellAdds");
        return this.addVariant(variantId);
      }
      if (e.target.closest("[data-sk-checkout]")) {
        this.statBump("checkoutClicks");
        window.location.href = "/checkout";
      }
    }

    onChange(e) {
      if (e.target.matches("[data-sk-protection]")) {
        this.toggleProtection(e.target.checked);
        return;
      }
      // Editable quantity input — type a number, updates on blur/enter (0 removes)
      if (e.target.matches(".sk-qty__input")) {
        const itemEl = e.target.closest(".sk-item");
        if (!itemEl) return;
        const line = Number(itemEl.dataset.line);
        const current = this.cart.items[line - 1] ? this.cart.items[line - 1].quantity : 1;
        let q = parseInt(e.target.value, 10);
        if (isNaN(q) || q < 0) q = current; // invalid → revert
        if (q === current) { e.target.value = current; return; }
        this.setLineQty(line, q);
      }
    }

    /* ===================================================================
       LOCAL STATS  (browser-only counters, no GA4/Meta — mirrors the
       upsell modal's ?sk_stats=1 viewer). View with ?sk_cart_stats=1 in
       the URL, or run sk_cart_stats() in the console.
       =================================================================== */
    statData() {
      try { return JSON.parse(localStorage.getItem(STATS_KEY)) || {}; }
      catch (e) { return {}; }
    }
    statBump(key, n = 1) {
      try {
        const d = this.statData();
        d[key] = (d[key] || 0) + n;
        localStorage.setItem(STATS_KEY, JSON.stringify(d));
      } catch (e) { /* private mode / quota — ignore */ }
    }
    showStatsViewer() {
      document.getElementById("sk-cart-stats")?.remove();
      const d = this.statData();
      const opens = d.opens || 0;
      const rate = (n) => (opens ? Math.round(((n || 0) / opens) * 100) + "%" : "—");
      const rows = [
        ["Cart opens", opens, ""],
        ["Upsell adds", d.upsellAdds || 0, rate(d.upsellAdds)],
        ["Free gifts unlocked", d.giftUnlocks || 0, rate(d.giftUnlocks)],
        ["Protection added", d.protectionAdds || 0, rate(d.protectionAdds)],
        ["Checkout clicks", d.checkoutClicks || 0, rate(d.checkoutClicks)],
      ];
      const tr = rows.map(
        ([l, v, p]) =>
          `<tr><td style="padding:7px 0;border-bottom:1px solid #2a2a2a">${l}</td>` +
          `<td style="text-align:right;font-variant-numeric:tabular-nums;font-weight:700;border-bottom:1px solid #2a2a2a">${v}</td>` +
          `<td style="text-align:right;color:#ff7a78;font-weight:700;border-bottom:1px solid #2a2a2a;padding-left:14px">${p}</td></tr>`
      ).join("");
      const box = document.createElement("div");
      box.id = "sk-cart-stats";
      box.style.cssText =
        "position:fixed;top:16px;right:16px;z-index:2147483647;background:#111;color:#fff;" +
        "font:13px/1.4 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;" +
        "padding:16px 18px 14px;border-radius:10px;box-shadow:0 8px 30px rgba(0,0,0,.4);width:280px";
      box.innerHTML =
        `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">` +
        `<strong style="font-size:12px;letter-spacing:.08em;text-transform:uppercase">Slider Cart Stats</strong>` +
        `<button data-x style="background:none;border:0;color:#888;font-size:18px;cursor:pointer;line-height:1">×</button></div>` +
        `<table style="width:100%;border-collapse:collapse">${tr}</table>` +
        `<div style="display:flex;gap:8px;margin-top:12px">` +
        `<button data-reset style="flex:1;background:#9b2624;border:0;color:#fff;padding:7px;border-radius:6px;cursor:pointer;font-weight:700;font-size:12px">Reset</button>` +
        `</div>` +
        `<p style="margin:10px 0 0;font-size:10px;color:#777">This browser only. Numbers persist across visits on this device.</p>`;
      box.querySelector("[data-x]").onclick = () => box.remove();
      box.querySelector("[data-reset]").onclick = () => {
        try { localStorage.removeItem(STATS_KEY); } catch (e) {}
        this.showStatsViewer();
      };
      document.body.appendChild(box);
    }

    /* ===================================================================
       OPEN / CLOSE
       =================================================================== */
    open(trigger) {
      const wasOpen = this.classList.contains("is-open");
      if (trigger) this.activeTrigger = trigger;
      this.classList.add("is-open");
      document.body.classList.add("sk-cart-locked");
      this.toggleWidgets(true);
      requestAnimationFrame(() => this.classList.add("is-anim"));
      if (!wasOpen) this.statBump("opens"); // count only closed→open transitions
    }

    close() {
      // Cancel any pending auto-open + start the dismissal window so the post-add
      // event/fetch cascade can't re-open the drawer the user just closed.
      clearTimeout(this._extOpenTimer);
      this._closedAt = Date.now();
      this.classList.remove("is-open", "is-anim");
      document.body.classList.remove("sk-cart-locked");
      this.toggleWidgets(false);
      if (this.activeTrigger && this.activeTrigger.focus) this.activeTrigger.focus();
    }

    // Hide floating widgets (chat, etc.) that would overlap the open cart.
    // Use visibility (display:none gets re-enforced by some chat widgets).
    toggleWidgets(hide) {
      const ids = ["ShopifyChat", "shopify-chat", "chat-widget-container", "tidio-chat"];
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        if (hide) {
          el.style.setProperty("visibility", "hidden", "important");
          el.style.setProperty("pointer-events", "none", "important");
        } else {
          el.style.removeProperty("visibility");
          el.style.removeProperty("pointer-events");
        }
      });
    }

    setLoading(on) {
      this.busy = on;
      this.classList.toggle("sk-busy", on);
    }

    flash(msg) {
      let n = this.querySelector(".sk-cart__flash");
      if (!n) {
        n = document.createElement("div");
        n.className = "sk-cart__flash";
        this.panel.appendChild(n);
      }
      n.textContent = msg;
      n.classList.add("show");
      clearTimeout(this._flashT);
      this._flashT = setTimeout(() => n.classList.remove("show"), 3000);
    }

    syncBubble() {
      const bubble = document.querySelector("#cart-icon-bubble [data-cart-count], .cart-count-bubble span, #cart-icon-bubble .cart-count-bubble");
      if (bubble && this.cart) bubble.textContent = this.displayCount();
    }

    /* ---- utils ---------------------------------------------------------- */
    imgUrl(url, w) {
      if (!url) return "";
      if (url.includes("?")) return `${url}&width=${w}`;
      return `${url}${url.includes("cdn.shopify") ? `?width=${w}` : ""}`;
    }

    escape(str) {
      return String(str == null ? "" : str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    money(cents) {
      const fmt = this.cfg.moneyFormat || "${{amount}}";
      const amount = (cents / 100).toFixed(2);
      const parts = amount.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      const withCommas = parts.join(".");
      const noDecimals = parts[0];
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(".", ","));
    }
  }

  customElements.define("sk-cart-drawer", SKCartDrawer);
})();
